/** Undo/redo for every canvas action (cs3 CONTRACT §2.9). An entry carries its own inverse. */
export interface HistoryContext {
  /** The live id for one an entry captured: a re-created filter or item gets a new id. */
  resolve: (id: string) => string;
  /** After a re-create: `oldId` now lives as `newId`. */
  alias: (oldId: string, newId: string) => void;
}
export type HistoryStep = (ctx: HistoryContext) => Promise<void>;
export interface HistoryEntryInput {
  /** Plain, sentence case: "Move 3 tiles", "Edit chart". The undo tooltip shows it. */
  label: string;
  /** Same key as the top entry, recorded within COALESCE_MS of it: folded into it. */
  coalesceKey?: string;
  /** Item ids it touches, as captured. */
  itemIds?: string[];
  /** `null` on either: can't be undone; recording it clears both stacks. */
  undo: HistoryStep | null;
  redo: HistoryStep | null;
  /** How a later entry with the same key folds in. Default: both run (undo newest first). */
  merge?: (next: HistoryEntryInput) => HistoryEntryInput;
}
export interface HistoryEntry extends HistoryEntryInput {
  id: string;
  at: number;
}
export type HistoryEvent =
  | { kind: "recorded"; entry: HistoryEntry }
  /** `resolvedItemIds`: the entry's item ids as they live now (a re-created tile has a new id). */
  | { kind: "undone" | "redone"; entry: HistoryEntry; resolvedItemIds: string[] }
  | { kind: "failed"; entry: HistoryEntry; error: unknown; direction: "undo" | "redo" }
  | { kind: "cleared"; reason: string | null };
export interface CanvasHistoryApi {
  record: (entry: HistoryEntryInput) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  isBusy: boolean;
  clear: (reason?: string) => void;
  /** Awaited before every undo/redo (the editor's pending autosave). Returns unregister. */
  registerFlusher: (flush: () => Promise<void>) => () => void;
  subscribe: (listener: (event: HistoryEvent) => void) => () => void;
}
export const HISTORY_LIMIT = 100;
export const COALESCE_MS = 5000;
/** A tile that is not an item yet (a chart being created): never persisted, never recorded. */
export const TRANSIENT_ID_PREFIX = "draft:";
export const isTransientItemId = (id: string): boolean => id.startsWith(TRANSIENT_ID_PREFIX);
